import React, { useMemo, useRef, useState, useEffect } from 'react';
import styles from './BotBranding.module.scss';
import SettingsHeader from 'pages/authenticated/layout/settingsLayout/settingsHeader/SettingsHeader';
import ConversationCard from './components/ConversationCard/ConversationCard';
import RoundedTabs from 'component/atom/Tabs/RoundedTabs';
import ConversationSettings from './components/ConversationSettings/ConversationSettings';
import GreetingsSettings from './components/GreetingsSettings/GreetingsSettings';
import EmptyConversationCard from './components/ConversationCard/EmptyConversationCard';
import * as Yup from 'yup';
import { Form, Formik, useFormikContext } from 'formik';
import { useNavigate, useParams } from 'react-router-dom';
import { usePost } from 'hooks/usePost';
import { settingsEndpoints } from 'hooks/endpoints';
import { useFetch } from 'hooks/useFetch';
import Loader from 'component/atom/loader/Loader';
import { useQueryClient } from '@tanstack/react-query';
// import Modal from 'component/hoc/modal/Modal';
import ConfirmationModal from 'component/hoc/success-modal/ConfirmationModal';

const hexColor = /^#(?:[0-9a-fA-F]{3}){1,2}$/;
// const imgTypes = [
//   'image/png',
//   'image/jpeg',
//   'image/jpg',
//   'image/svg+xml',
//   'image/webp',
// ];
// const MAX_ICON_BYTES = 2 * 1024 * 1024;

const botBrandingSchema = Yup.object({
  BotId: Yup.string()
    .uuid('Must be a valid UUID')
    .required('BotId is required'),
  ChatbotName: Yup.string()
    .trim()
    .max(30, 'Max 30 characters')
    .matches(
      /^[a-zA-Z0-9\s_-]+$/,
      'Only letters, numbers, spaces, and underscores are allowed'
    )
    .required('Chatbot Name is required'),
  ChatIcon: Yup.mixed().nullable(),
  IconBackground: Yup.string()
    .trim()
    .matches(hexColor, 'Use a hex color like #0957D0')
    .nullable(),
  Theme: Yup.string().trim().nullable(),
  HeaderStyle: Yup.string().nullable(),
  TitlePosition: Yup.string().nullable(),
  Style: Yup.string().trim().nullable(),
  AccentColor: Yup.string()
    .trim()
    .matches(hexColor, 'Use a hex color like #0957D0')
    .nullable(),
  IconColorPrimary: Yup.string()
    .trim()
    .matches(hexColor, 'Use a hex color')
    .nullable(),
  IconColorSecondary: Yup.string()
    .trim()
    .matches(hexColor, 'Use a hex color')
    .nullable(),
  FontFamily: Yup.string().trim().nullable(),
  PlaceholderText: Yup.string()
    .trim()
    .max(80, 'Max 80 characters')
    .required('Placeholder Text is required'),
  GreetingMessage: Yup.string()
    .trim()
    .required('Greeting Message is required')
    .max(15, 'Max 15 characters'),
  GreetingStyle: Yup.string().nullable(),
  GreetingAccentColor: Yup.string()
    .trim()
    .matches(hexColor, 'Use a hex color like #0957D0')
    .nullable(),
  ShowChatIcon: Yup.boolean().nullable(),
  ShowIconBackground: Yup.boolean().nullable(),
});

function toFormData(data) {
  const fd = new FormData();
  const appendDeep = (key, value) => {
    if (value === null || typeof value === 'undefined') return;
    if (value instanceof File || value instanceof Blob) {
      fd.append(key, value, value.name ?? 'file');
      return;
    }
    if (Array.isArray(value)) {
      value.forEach((v, i) => appendDeep(`${key}[${i}]`, v));
      return;
    }
    if (typeof value === 'object') {
      Object.keys(value).forEach((k) => appendDeep(`${key}[${k}]`, value[k]));
      return;
    }
    fd.append(key, String(value));
  };
  Object.keys(data || {}).forEach((k) => appendDeep(k, data[k]));
  return fd;
}

export const mapApiToForm = (api = {}, fallbackId) => ({
  BotId: api?.botId ?? fallbackId ?? '',
  ChatbotName: api?.chatbotName ?? 'My Chatbot',
  ChatIcon: api?.chatIcon ?? '/src/svg/bot.svg',
  IconBackground: api?.iconBackground ?? '#ffffff',

  Theme: api?.theme ? String(api.theme).toLowerCase() : 'white',
  HeaderStyle: api?.headerStyle ?? 'Text',
  TitlePosition: api?.titlePosition ? String(api.titlePosition) : 'Left',
  Style: api?.style ?? 'gradient',

  AccentColor: api?.accentColor ?? '#000000',
  IconColorPrimary: api?.iconColorPrimary ?? '#000000',
  IconColorSecondary: api?.iconColorSecondary ?? '#666666',
  FontFamily: api?.fontFamily ?? 'Helvetica Neue',
  PlaceholderText: api?.placeholderText ?? 'Type your message...',
  GreetingMessage: api?.greetingMessage ?? 'Ask AiDucator',
  GreetingStyle: api?.greetingStyle
    ? String(api.greetingStyle).toLowerCase()
    : 'gradient',
  GreetingAccentColor: api?.greetingAccentColor ?? '#000000',

  ShowChatIcon: api?.showChatIcon ?? true,
  ShowIconBackground: api?.showIconBackground ?? true,
});
const DirtyTracker = ({ onChange }) => {
  const { dirty } = useFormikContext();
  useEffect(() => {
    onChange(!!dirty);
  }, [dirty, onChange]);
  return null;
};

const BotBranding = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [externalActiveTab, externalSetActiveTab] = useState(0);
  const formikRef = useRef(null);
  const queryClient = useQueryClient();
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [href, setHref] = useState(null);
  const {
    data: botBranding,
    refetch,
    isLoading,
    isFetching,
  } = useFetch(
    ['botBranding', id],
    settingsEndpoints.getBotBranding(),
    { enable: true },
    { BotId: id }
  );
  const { mutate: saveBranding, isPending } = usePost({
    onErrorToast: 'Something went wrong',
  });

  const api = botBranding?.data;

  const initialValues = useMemo(() => mapApiToForm(api, id), [api, id]);

  const tabs = [
    { label: 'Conversation', content: <ConversationSettings /> },
    { label: 'Greetings', content: <GreetingsSettings /> },
  ];

  useEffect(() => {
    if (!isDirty) return;

    const handleDocumentClick = (e) => {
      const anchor = e.target.closest('a');
      if (!anchor) return;

      if (
        e.defaultPrevented ||
        (anchor.target && anchor.target !== '_self') ||
        e.metaKey ||
        e.ctrlKey ||
        e.shiftKey ||
        e.altKey
      ) {
        return;
      }

      const href = anchor.getAttribute('href');
      if (!href) return;
      setHref(href);
      if (href.startsWith('#')) return;

      const url = new URL(href, window.location.origin);

      if (url.origin === window.location.origin) {
        const current =
          window.location.pathname +
          window.location.search +
          window.location.hash;
        const next = url.pathname + url.search + url.hash;

        if (current !== next) {
          e.preventDefault();
          setShowSaveModal(true);
        }
      }
    };

    document.addEventListener('click', handleDocumentClick, true);
    return () =>
      document.removeEventListener('click', handleDocumentClick, true);
  }, [isDirty]);

  useEffect(() => {
    if (!isDirty) return;

    const onPopState = () => {
      setShowSaveModal(true);
      window.history.pushState(null, '', window.location.href);
    };

    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [isDirty]);

  return (
    <>
      <Formik
        innerRef={formikRef}
        initialValues={initialValues}
        enableReinitialize
        validationSchema={botBrandingSchema}
        onSubmit={(values, { resetForm }) => {
          const formData = toFormData(values);
          saveBranding(
            { url: settingsEndpoints.setBotBranding(), data: formData },
            {
              onSuccess: () => {
                resetForm();
                refetch();
                queryClient.invalidateQueries({ queryKey: ['botsData'] });
              },
            }
          );
        }}
      >
        <div className={styles['branding-container']}>
          <DirtyTracker onChange={setIsDirty} />

          <SettingsHeader
            title='Branding'
            rightButtons={[
              {
                label: 'Save',
                type: 'save',
                opicBackground: true,
                isLoading: isPending,
                func: () => formikRef.current?.submitForm(),
              },
            ]}
          />
          {isLoading || isFetching ? (
            <div className={styles.loader}>
              <Loader style={{ width: '15px', height: '15px' }} isComponent />
            </div>
          ) : (
            <div className={styles['branding-body']}>
              <Form
                encType='multipart/form-data'
                className={styles['form-container']}
              >
                {externalActiveTab === 0 ? (
                  <ConversationCard />
                ) : (
                  <EmptyConversationCard />
                )}
              </Form>
              <div className={styles['tabs-container']}>
                <RoundedTabs
                  tabs={tabs}
                  activeTab={externalActiveTab}
                  setActiveTab={externalSetActiveTab}
                />
              </div>
            </div>
          )}
        </div>
      </Formik>
      <ConfirmationModal
        isOpen={showSaveModal}
        onClose={() => {
          navigate(href);
        }}
        onSubmit={() => {
          formikRef.current?.submitForm();
          setShowSaveModal(false);
        }}
        closeOnSubmit={true}
        onApproveButton={{ text: 'save changes' }}
        text='Unsaved Changes'
        subText='You have unsaved changes. Do you want to save them before leaving the Branding page?'
        subTextClassName={styles.modalSubText}
        modalContentClassName={styles.modalContentClassName}
        buttonClassName={styles.buttonClassName}
        closeBtnContainerClassName={styles.closeBtnContainer}
        closeBtnClassName={styles.closeBtn}
        isUpdate={true}
        type='save'
      />
    </>
  );
};

export default BotBranding;
